import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const GA_MEASUREMENT_ID = 'G-5QZ68H5VFM';

/**
 * Check if user accepted analytics cookies
 */
const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) return false;
    const prefs = JSON.parse(consent);
    return prefs.analytics === true;
  } catch {
    return false;
  }
};

/**
 * Load gtag.js script once
 */
let gtagLoaded = false;
const loadGtag = () => {
  if (gtagLoaded) return;
  if (document.querySelector(`script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
    gtagLoaded = true;
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  gtagLoaded = true;
};

/**
 * useGA4 — Google Analytics 4 hook for SPA
 * - Respects cookie consent (analytics category)
 * - Tracks page views on route change
 * - Loads script async, only once
 * - Listens for consent changes via storage events
 */
export const useGA4 = () => {
  const location = useLocation();
  const lastPath = useRef('');
  const [consented, setConsented] = useState(hasAnalyticsConsent);

  // Listen for cookie consent changes (same tab + other tabs)
  useEffect(() => {
    const checkConsent = () => setConsented(hasAnalyticsConsent());
    window.addEventListener('storage', checkConsent);
    window.addEventListener('cookieConsentChanged', checkConsent);

    // Also poll briefly after mount in case consent was just given
    const t = setTimeout(checkConsent, 1000);
    return () => {
      window.removeEventListener('storage', checkConsent);
      window.removeEventListener('cookieConsentChanged', checkConsent);
      clearTimeout(t);
    };
  }, []);

  // Load gtag when consent is granted
  useEffect(() => {
    if (consented) loadGtag();
  }, [consented]);

  // Track page views on route change
  useEffect(() => {
    if (!consented || !window.gtag) return;
    if (location.pathname.startsWith('/admin')) return;

    const fullPath = location.pathname + location.search;
    if (lastPath.current === fullPath) return;
    lastPath.current = fullPath;

    // Small delay to let page title update
    const t = setTimeout(() => {
      window.gtag('event', 'page_view', {
        page_path: fullPath,
        page_title: document.title,
        page_location: window.location.href,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, consented]);
};

export default useGA4;
